import { useNavigate } from 'react-router-dom';
import { Virtuoso } from 'react-virtuoso';
import { ArrowRightSmall, ChatFill, ChevronDownSmall, LikeFill } from '@peacecover/icon';
import { t } from 'i18next';
import { useGetSceneSubscriptions } from '../../../domains/scene/ui/hooks/useGetSceneSubscriptions';
import { useSubscriptionFilter, type FilterType } from '../hooks/useSubscriptionFilter';
import { getChatDirection } from '../utils/getChatDirection';
import { getSceneTags } from '../utils/getSceneTags';
import EmptyCase from '../../../components/errors/EmptyCase';
import PreviewHeaderItem from './PreviewHeaderItem';
import ThreadContentItem from './ThreadContentItem';
import ChatContentItem from './ChatContentItem';
import PreviewCastRelationship from './PreviewCastRelationship';
import { MenuDropdown, MenuDropdownItem } from '../../components/popup/MenuDropdown';
import { ReactComponent as ThreadIcon } from '../../../icons/thread.svg';
import { ROUTES } from '../../../constants/routes';
import type { components } from '../../../types/schema';

type SceneSubscription = components['schemas']['ApiV1UserSceneSubscription'];
type ThreadLastReadContent = components['schemas']['ApiV1AdaptedFictionThreadContent'];
type ChatLastReadContent = components['schemas']['ApiV1AdaptedFictionChatContent'];

const FILTER_OPTIONS: FilterType[] = ['all', 'recent', 'liked'];

const PreviewSubscriptionList = () => {
  const { filter, handleFilterSelect, isMenuDropdownOpen, setIsMenuDropdownOpen } =
    useSubscriptionFilter();

  const { data, fetchNextPage, hasNextPage, isFetching, isLoading } = useGetSceneSubscriptions(
    ['SceneThread', 'SceneChat'],
    filter === 'liked'
  );
  const previewList = data?.pages?.flatMap((page) => page.userSceneSubscriptions);

  const loadMore = () => {
    if (isFetching || !hasNextPage) return;
    fetchNextPage();
  };

  if (isLoading) {
    return null;
  }

  return (
    <div className="h-full flex flex-col">
      <FilterHeader
        filter={filter}
        isMenuDropdownOpen={isMenuDropdownOpen}
        setIsMenuDropdownOpen={setIsMenuDropdownOpen}
        handleFilterSelect={handleFilterSelect}
      />
      <div className="flex-1 min-h-0">
        {previewList?.length === 0 ? (
          // @ts-ignore
          <EmptyCase emoji="😳" title={t('SceneMain.subscription.emptyTitle')} />
        ) : (
          <Virtuoso
            overscan={480}
            defaultItemHeight={260}
            style={{ height: '100%' }}
            data={previewList}
            endReached={loadMore}
            atBottomThreshold={200}
            itemContent={(_index, subscription: SceneSubscription) => (
              <PreviewSubscriptionItem subscription={subscription} />
            )}
          />
        )}
      </div>
    </div>
  );
};

export default PreviewSubscriptionList;

const PreviewSubscriptionItem = ({ subscription }: { subscription: SceneSubscription }) => {
  const navigate = useNavigate();
  const lastRead = subscription?.lastReadContent;
  const threadContent =
    subscription?.contentType === 'SceneThread' ? (lastRead as ThreadLastReadContent) : null;
  const chatContent =
    subscription?.contentType !== 'SceneThread' ? (lastRead as ChatLastReadContent) : null;

  return (
    <div className="pt-[20px]">
      <div className="px-[22px] flex items-center justify-between">
        <PreviewCastRelationship
          castRelationship={subscription?.castRelationship ?? null}
          className="text-[#FFF]"
        />
        <div className="flex items-center gap-[4px]">
          {subscription?.contentType === 'SceneThread' ? (
            <ThreadIcon width={14} height={14} />
          ) : (
            <ChatFill size={14} className="text-pure-white" />
          )}
          <p className="text-[12px] font-medium leading-[14px] text-pure-white">
            {subscription?.contentType === 'SceneThread'
              ? t('SceneMain.subscription.thread')
              : t('SceneMain.subscription.chat')}{' '}
            {subscription?.currentSeq}/{subscription?.count}
          </p>
        </div>
      </div>
      <PreviewHeaderItem
        tags={getSceneTags(subscription ?? null)}
        className="px-[22px] mt-[16px]"
      />
      {threadContent !== null ? (
        <ThreadContentItem
          speaker={threadContent?.speaker ?? undefined}
          threadType={threadContent?.threadType}
          text={threadContent?.text ?? ''}
          className="mt-[8px]"
          hasLine={false}
        />
      ) : chatContent !== null ? (
        <ChatContentItem
          chatType={chatContent?.chatType}
          text={chatContent?.text ?? ''}
          pictureUrl={chatContent?.pictureUrl ?? ''}
          direction={getChatDirection(
            subscription?.castRelationship ?? null,
            chatContent?.speaker ?? ''
          )}
          className={`mt-[17px] ${chatContent?.chatType === 'dialogue' ? '!mx-[22px]' : ''}`}
        />
      ) : null}
      <div className="border-t-[1px] border-b-[5px] border-[#171717] flex items-center justify-between px-[22px] py-[14px]">
        <div className="flex items-center gap-[12px]">
          <div className="flex items-center gap-[4px]">
            <LikeFill
              size={18}
              className="text-pure-white"
              gradientStops={
                subscription?.isLiked
                  ? [
                      { offset: '0%', color: '#FF45A9' },
                      { offset: '100%', color: '#FF6A6A' }
                    ]
                  : []
              }
            />
            <p className="text-[11px] font-semibold leading-[13px] text-pure-white">
              {subscription?.likesCount}
            </p>
          </div>
          <div className="flex items-center gap-[4px] text-pure-white">
            <ChatFill size={18} />
            <p className="text-[11px] font-semibold leading-[13px]">
              {subscription?.commentsCount}
            </p>
          </div>
        </div>
        <button
          type="button"
          className="text-pure-white text-[12px] font-medium leading-[14px] flex items-center gap-[4px]"
          onClick={() =>
            navigate(
              `${ROUTES.SCENE.PREVIEW_DETAIL.GO(subscription.sceneId ?? '')}?cursor=${subscription.contentId}`
            )
          }
        >
          {t('SceneMain.subscription.continue')}
          <ArrowRightSmall size={14} />
        </button>
      </div>
    </div>
  );
};

const FilterHeader = ({
  filter,
  isMenuDropdownOpen,
  setIsMenuDropdownOpen,
  handleFilterSelect
}: {
  filter: FilterType;
  isMenuDropdownOpen: boolean;
  setIsMenuDropdownOpen: (isOpen: boolean) => void;
  handleFilterSelect: (newFilter: FilterType) => void;
}) => (
  <div className="flex items-center justify-end gap-[5px] mt-[20px] mb-[10px] px-[24px] relative">
    <p
      className="text-[15px] font-semibold leading-[18px] text-pure-white cursor-pointer"
      onClick={() => setIsMenuDropdownOpen(!isMenuDropdownOpen)}
    >
      {t(`SceneMain.subscription.filter.${filter}`)}
    </p>
    <ChevronDownSmall
      size={18}
      className="text-pure-white"
      onClick={() => setIsMenuDropdownOpen(!isMenuDropdownOpen)}
    />
    {isMenuDropdownOpen && (
      <MenuDropdown
        className="absolute top-[45px] right-[13px] z-10"
        onClickOutside={() => setIsMenuDropdownOpen(false)}
      >
        {FILTER_OPTIONS.map((option) => (
          <MenuDropdownItem
            key={option}
            isActive={filter === option}
            onClick={() => handleFilterSelect(option)}
          >
            {t(`SceneMain.subscription.filter.${option}`)}
          </MenuDropdownItem>
        ))}
      </MenuDropdown>
    )}
  </div>
);
